<template>
	<div ref="shorts-section" class="shorts-section">
		<div class="title">
			<span>{{ title }}</span>
			<div v-if="pageList.length > 1" class="nav">
				<span>{{ selectedPageIndex + 1 }} / {{ pageList.length }}</span>
				<div class="nav-dots">
					<div
						v-for="i in pageList.length"
						:key="i"
						class="dot"
						:class="{ selected: i - 1 === selectedPageIndex }"
						@click="setPageIndex(i - 1, true)"
					></div>
				</div>
				<div v-if="!isMobile" class="arrow previous" :class="{ disabled: !canNavPrevious }" @click="navPreviousPage"></div>
				<div v-if="!isMobile" class="arrow next" :class="{ disabled: !canNavNext }" @click="navNextPage"></div>
			</div>
		</div>
		<div v-if="pageList.length > 0" ref="shorts" class="shorts-wrapper">
			<transition :name="`shorts-${transitionName}`" tag="div" mode="out-in">
				<div :key="selectedPageIndex" class="pages">
					<div class="section" :class="{ ieMode: isIE }">
						<div
							v-for="(page, $index) in pageList[selectedPageIndex]"
							:key="page.index"
							class="preview"
							@mouseenter="hovered = !isMobile ? $index : -1"
							@mouseleave="hovered = -1"
							@click="showPreview(page.index)"
						>
							<img :src="`https://i.ytimg.com/vi/${getLocaleString(page.Image)}/oar2.jpg`" />
							<svgIcon v-if="hovered !== $index" icon-holder-class="play" icon-id="icon-btn-play" />
							<svgIcon v-if="hovered === $index" icon-holder-class="play" icon-id="icon-btn-play-hover" />
						</div>
						<template v-if="selectedPageIndex === pageList.length - 1">
							<div v-for="space in emptySpaceList" :key="`empty-${space}`" class="empty"></div>
						</template>
					</div>
				</div>
			</transition>
		</div>
		<Lightbox
			v-if="showLightbox"
            class="shorts-lightbox"
			:media-list="processedMediaList"
			:selected-preview-index="selectedPreviewIndex"
			@setPageIndex="setPageIndex"
			@track="track"
			@close="closeLightbox"
		></Lightbox>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { getLocaleString } from '../../../plugins/helpers';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import Lightbox from './lightbox';
import { locale } from '../../../config/config-cdn';

export default {
	name: 'GameDetailsMediaSection',
	components: {
		svgIcon,
		Lightbox,
	},
	props: {
		shorts: {
			type: Object,
			default: () => [],
		},
		game: {
			type: Object,
			default: () => {},
		},
	},
	data() {
		return {
			processedMediaList: [],
			pageList: [],
			emptySpaceList: [],
			previousSelectedPageIndex: 0,
			selectedPageIndex: 0,
			xDown: null,
			yDown: null,
			hovered: -1,
			showLightbox: false,
			selectedPreviewIndex: -1,
			title: '',
			viewShortsCount: 6,
		};
	},
	computed: {
		...mapState(['isIOS', 'isAndroid', 'isIE']),
		canNavNext() {
			return this.selectedPageIndex < this.pageList.length - 1;
		},
		canNavPrevious() {
			return this.selectedPageIndex > 0;
		},
		transitionName() {
			return this.selectedPageIndex >= this.previousSelectedPageIndex ? 'next' : 'previous';
		},
		isMobile() {
			return this.isIOS || this.isAndroid;
		},
		language() {
			return this.$i18n.locale;
		},
	},
	mounted() {
		this.$emit('setRef', 'shorts', this.$refs['shorts-section']);
		this.setPageInfo();
		this.setEmptySpaceList();
		this.$nextTick(() => {
			this.$refs.shorts.addEventListener('touchstart', this.handleTouchStart, false);
			this.$refs.shorts.addEventListener('touchmove', this.handleTouchMove, false);
		});
	},
	methods: {
		getLocaleString(data) {
			return getLocaleString(data, this.language);
		},
		setPageIndex(index, jump) {
			this.previousSelectedPageIndex = this.selectedPageIndex;
			this.selectedPageIndex = index;
			if (jump) this.track('Jump');
		},
		navNextPage() {
			if (!this.canNavNext) return;
			this.track('Right');
			this.setPageIndex(this.selectedPageIndex + 1);
		},
		navPreviousPage() {
			if (!this.canNavPrevious) return;
			this.track('Left');
			this.setPageIndex(this.selectedPageIndex - 1);
		},
		handleTouchStart(e) {
			const { clientX, clientY } = e.touches[0];
			this.xDown = clientX;
			this.yDown = clientY;
		},
		handleTouchMove(e) {
			if (!this.xDown || !this.yDown || this.pageList.length <= 1) return;

			let xUp = e.touches[0].clientX;
			let yUp = e.touches[0].clientY;

			let xDiff = this.xDown - xUp;
			let yDiff = this.yDown - yUp;

			if (Math.abs(xDiff) > Math.abs(yDiff)) {
				if (xDiff > 0) {
					this.navNextPage();
				} else {
					this.navPreviousPage();
				}
			}

			this.xDown = null;
			this.yDown = null;
		},
		showPreview(index) {
			this.selectedPreviewIndex = index;
			this.showLightbox = true;
			this.track('Open Lightbox');
		},
		closeLightbox() {
			this.showLightbox = false;
			this.track('Close Lightbox');
		},
		setPageInfo() {
			const { Shorts, TabTitle } = this.shorts.media;
			this.title = getLocaleString(TabTitle) || 'Shorts';
			let lastPageIndex = 0;
			this.pageList = [];
			const shortsList = getLocaleString(Shorts);
			for (let i = 0; i < shortsList.length; i++) {
				const isBreakpoint = i % this.viewShortsCount === 0;
				if (isBreakpoint) {
					lastPageIndex = i / this.viewShortsCount;
					this.pageList.push([]);
				}
				const shortsId = shortsList[i];
				const media = {};
				media['index'] = i;
				media['pageIndex'] = lastPageIndex;
				media['Type'] = 'youtube';
				media['Image'] = { [locale[this.language]]: shortsId };
				this.processedMediaList.push(media);
				this.pageList[lastPageIndex].push(media);
			}
		},
		setEmptySpaceList() {
			this.emptySpaceList = [];
			const shortsList = getLocaleString(this.shorts.media.Shorts);
			const len = shortsList.length;

			if (len % this.viewShortsCount !== 0) {
				for (let i = 0; i < this.viewShortsCount - (len % this.viewShortsCount); i++) {
					this.emptySpaceList.push(i);
				}
			}
		},
		track(gameDetailMediaInteraction) {
			dataLayer.push({
				event: 'Shorts',
				gameDetailMediaInteraction,
				gameDetailName: this.game.name,
				gameDetailID: this.game.productID,
			});
		},
	},
};
</script>

<style lang="less" scoped>
.shorts-previous-enter-active,
.shorts-next-enter-active {
	transition: all 1s;
}
.shorts-previous-enter {
	opacity: 0;
	transform: translateX(-50px);
}
.shorts-next-enter {
	opacity: 0;
	transform: translateX(50px);
}
.shorts-previous-enter-to,
.shorts-next-enter-to {
	opacity: 1;
}

.shorts-section {
	height: 100%;
	padding: 0 20px;
	width: 100%;
	margin-top: 50px;
	.title {
		align-items: center;
		color: white;
		display: flex;
		font-size: 20px;
		height: 40px;
		justify-content: space-between;
		letter-spacing: -0.4px;
		margin-bottom: 15px;
		user-select: none;
		.nav {
			align-items: center;
			color: rgba(255, 255, 255, 0.5);
			display: flex;
			font-size: 16px;
			.nav-dots {
				display: none;
			}
			span {
				margin-right: 6px;
			}
			.arrow {
				background-color: rgba(0, 0, 0, 0.5);
				border: 1px solid rgba(255, 255, 255, 0.2);
				border-radius: 5px;
				cursor: pointer;
				height: 40px;
				position: relative;
				transition: all 0.2s;
				width: 31px;
				&.disabled {
					cursor: default;
					&:before {
						opacity: 0.2;
					}
				}
				&:not(:active):not(.disabled):hover {
					background-color: rgba(0, 0, 0, 1);
					border: 1px solid rgba(255, 255, 255, 0.7);
					&:before {
						opacity: 1;
					}
				}
				&:active:not(.disabled) {
					&:before {
						opacity: 0.5;
					}
				}
				&:before {
					background-position: center;
					background-repeat: no-repeat;
					content: '';
					height: 100%;
					opacity: 0.8;
					position: absolute;
					width: 100%;
				}
				&.previous {
					&:before {
						background-image: url('~@/images/game-details/arrows/previous.svg');
					}
					margin: 0 2px 0 0;
				}
				&.next {
					&:before {
						background-image: url('~@/images/game-details/arrows/next.svg');
					}
				}
			}
		}
	}
	.pages {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		margin: auto;
		.section {
			align-items: flex-start;
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;
			width: 332px;
			margin: auto;
			&.ieMode {
				.play {
					margin-left: -29px;
					margin-top: -29px;
				}
			}
			.preview {
				align-items: center;
				border-radius: 5px;
				cursor: pointer;
				display: flex;
				height: 150px;
				width: 104px;
				justify-content: center;
				overflow: hidden;
				position: relative;
				background-color: #000;
				&:nth-child(1),
				&:nth-child(2),
				&:nth-child(3) {
					margin-bottom: 10px;
				}
				&:not(.mobile):hover {
					border: 4px solid #0077ff;
					img {
						left: -4px;
						width: calc(100% + 8px);
					}
				}
				.play {
					height: 50px;
					position: absolute;
					width: 50px;
				}
				img {
					left: 0;
					position: absolute;
					width: 100%;
				}
			}
			.empty {
				height: 150px;
				width: 104px;
			}
		}
	}
}

@media (min-width: 768px) {
	.shorts-section {
		.title {
			margin: 0 auto 10px;
			.nav {
				.nav-dots {
					align-items: center;
					display: flex;
					height: 100%;
					justify-content: center;
					margin-right: 20px;
					.dot {
						background-color: white;
						cursor: pointer;
						height: 6px;
						opacity: 0.5;
						transition: opacity 0.2s;
						width: 6px;
						&:not(:last-child) {
							margin-right: 7px;
						}
						&.selected,
						&:hover {
							opacity: 1;
						}
					}
				}
				span {
					display: none;
				}
			}
		}
		.pages {
			flex-direction: row;
			height: 162px;
			width: 728px;
			.section {
				height: 162px;
				width: 728px;
				.preview {
					height: 162px;
					width: 113px;
					&:nth-child(1),
					&:nth-child(2),
					&:nth-child(3) {
						margin-bottom: 0;
					}
				}
				.empty {
					height: 162px;
					width: 113px;
				}
			}
		}
	}
}

@media (min-width: 1024px) {
	.shorts-section {
		.title {
			font-size: 16px;
			height: 40px;
			letter-spacing: -0.32px;
			width: 770px;
		}
		.shorts-wrapper {
			height: 171px;
		}
		.pages {
			height: 171px;
			width: 770px;
			.section {
				height: 171px;
				width: 770px;
				.preview {
					.play {
						height: 60px;
						width: 60px;
					}
				}
				.empty {
					height: 171px;
					width: 119px;
				}
			}
		}
	}
}

@media (min-width: 1280px) {
	.shorts-section {
		.title {
			width: 960px;
		}
		.shorts-wrapper {
			height: 213px;
		}
		.pages {
			height: 213px;
			width: 960px;
			.section {
				height: 213px;
				width: 960px;
				.preview,
				.empty {
					height: 213px;
					width: 149px;
				}
			}
		}
	}
}

@media (min-width: 1440px) {
	.shorts-section {
		margin-top: 80px;
		.title {
			font-size: 20px;
			letter-spacing: -0.4px;
			margin-bottom: 20px;
			width: 1080px;
		}
		.shorts-wrapper {
			height: 242px;
		}
		.pages {
			height: 242px;
			width: 1080px;
			.section {
				height: 242px;
				width: 1080px;
				.preview,
				.empty {
					height: 242px;
					width: 168px;
				}
			}
		}
	}
}

@media (min-width: 1600px) {
	.shorts-section {
		margin-top: 65px;
		.title {
			width: 1200px;
		}
		.shorts-wrapper {
			height: 270px;
		}
		.pages {
			height: 270px;
			width: 1200px;
			.section {
				height: 270px;
				width: 1200px;
				.preview,
				.empty {
					height: 270px;
					width: 187px;
				}
			}
		}
	}
}

@media (min-width: 1920px) {
	.shorts-section {
		margin-top: 65px;
		.title {
			width: 1440px;
		}
		.shorts-wrapper {
			height: 321px;
		}
		.pages {
			height: 321px;
			width: 1440px;
			.section {
				height: 321px;
				width: 1440px;
				.preview {
					height: 321px;
					width: 223px;
				}
			}
		}
	}
}

.shorts-lightbox {
    outline: none;
    &::v-deep {
        iframe {
            width: 25vw !important;
            height: auto !important;
            aspect-ratio: 9/16 !important;
            min-width: 345px !important;
        }
    }
}
</style>
